/* global GLightbox */
/* eslint-disable max-lines, max-lines-per-function */
import { api } from "jiffy-api";
import { isTouchDevice } from "common/utils/browser";
import { track } from "jiffy-analytics";
import initScroll from "common/utils/ui/slider";
import { loadGLightbox } from "common/glightbox-loader";
import "./review-image-carousel.scss";

const ANIMATION_DURATION = 600;
let inAnimation = false;
let $currentLink;

// Getters
const $getLightBoxLinks = () => document.querySelectorAll(".js-review-image-carousel-lightbox");
const $getCarouselWrapper = () => document.querySelector(".js-review-image-carousel");
const $getCarouselGrid = () =>
  $getCarouselWrapper().querySelector(".js-review-image-carousel-grid");
const $getSpinner = () => $getCarouselWrapper().querySelector(".js-review-image-carousel-spinner");
const $getMobileArrows = () => ({
  $leftArrow: $getCarouselWrapper().querySelector(".js-review-image-mobile-carousel-left"),
  $rightArrow: $getCarouselWrapper().querySelector(".js-review-image-mobile-carousel-right")
});
const $getDesktopArrows = () => ({
  $leftArrow: $getCarouselWrapper().querySelector(".js-review-image-carousel-left"),
  $rightArrow: $getCarouselWrapper().querySelector(".js-review-image-carousel-right")
});
const $getArrows = isTouch => ({
  $leftArrow: isTouch ? $getMobileArrows().$leftArrow : $getDesktopArrows().$leftArrow,
  $rightArrow: isTouch ? $getMobileArrows().$rightArrow : $getDesktopArrows().$rightArrow
});

// Actions
const trackEvent = suffix => {
  track(`review_image_carousel_${suffix}`, JSON.parse($currentLink.dataset.mixpanelData));
};
const stopTrackPropagination = () => {
  inAnimation = true;

  setTimeout(() => {
    inAnimation = false;
  }, ANIMATION_DURATION);
};

const loadMore = async $container => {
  if (window.allReviewsLoaded) return;

  const $spinner = $getSpinner();
  $spinner?.classList?.toggle("hidden", false);
  window.reviewsPage ||= 0;
  window.reviewsPage += 1;
  const { realProductId } = document.querySelector(".js-product-details").dataset;
  const { reviews } = await api.product.fetchMoreDecoratorReviews({
    page: window.reviewsPage,
    product_id: realProductId
  });
  if (reviews === "") {
    window.allReviewsLoaded = true;
  }
  // eslint-disable-next-line no-param-reassign
  $container.innerHTML += reviews;
  $spinner?.classList?.toggle("hidden", true);
  initGLightbox();
};

const checkArrowsVisibility = async () => {
  const { $leftArrow, $rightArrow } = $getArrows(isTouchDevice());
  const $carousel = $getCarouselGrid();
  const isStartOfScroll = $carousel.scrollLeft === 0;
  let isEndOfScroll = $carousel.scrollLeft >= $carousel.scrollWidth - $carousel.clientWidth - 5;

  if (isEndOfScroll) {
    await loadMore($carousel);
    isEndOfScroll = $carousel.scrollLeft >= $carousel.scrollWidth - $carousel.clientWidth - 5;
  }

  $leftArrow.classList.toggle("hidden", isStartOfScroll);
  $rightArrow.classList.toggle("hidden", isEndOfScroll);
};

const checkTouchBrowsers = () => {
  const { $leftArrow, $rightArrow } = $getArrows(!isTouchDevice());
  $leftArrow.remove();
  $rightArrow.remove();
};

// Tracks
const trackCloseModal = () => {
  trackEvent("close_modal_window");
};
const trackPrevImage = (suffix = "click") => {
  if (!$currentLink || inAnimation) return;

  const $prevElement = $currentLink.parentElement.previousElementSibling;
  if (!$prevElement) return;

  stopTrackPropagination();
  $currentLink = $prevElement.querySelector("a");
  trackEvent(`${suffix}_prev_slide`);
};
const trackNextImage = (suffix = "click") => {
  if (!$currentLink || inAnimation) return;

  const $nextElement = $currentLink.parentElement.nextElementSibling;
  if (!$nextElement) return;

  stopTrackPropagination();
  $currentLink = $nextElement.querySelector("a");
  trackEvent(`${suffix}_next_slide`);
};

const handleMobileCarouselScroll = () => {
  $getCarouselGrid().addEventListener("scroll", checkArrowsVisibility, { passive: true });
};

// Inits
const initNativeScroll = () => {
  checkArrowsVisibility();
  handleMobileCarouselScroll();
};

const initMixPanelEvents = () => {
  const $links = $getLightBoxLinks();

  $links.forEach($link => {
    $link.addEventListener("click", () => {
      $currentLink = $link;
    });
  });

  window.addEventListener(
    "click",
    event => {
      if (!$currentLink) return;

      const $handler = event.target;
      const isClosesHandler = ["lb-loader", "lb-close", "lightbox", "lightboxOverlay"].some(cn =>
        $handler.classList.contains(cn)
      );

      if (isClosesHandler) {
        trackCloseModal();
        $currentLink = null;
      }

      if ($handler.classList.contains("lb-next")) trackNextImage();
      if ($handler.classList.contains("lb-prev")) trackPrevImage();
    },
    { passive: true, capture: true }
  );

  window.addEventListener(
    "keydown",
    event => {
      if (!$currentLink) return;

      if (event.key === "Escape") {
        trackCloseModal();
        $currentLink = null;
      }

      if (event.key === "ArrowLeft") trackPrevImage("keyboard");
      if (event.key === "ArrowRight") trackNextImage("keyboard");
    },
    { passive: true }
  );
};

const initGLightbox = () => {
  function initializeGLightbox() {
    GLightbox({
      selector: ".carousel-glightbox",
      touchNavigation: true,
      loop: false,
      autoplayVideos: false
    });
  }

  loadGLightbox(initializeGLightbox);
};

export default () => {
  if (!$getCarouselWrapper()) return;

  checkTouchBrowsers();
  initNativeScroll();
  initMixPanelEvents();

  if (!isTouchDevice()) {
    initScroll({
      parent: document,
      list: ".js-review-image-carousel",
      grid: ".js-review-image-carousel-grid",
      arrowLeft: ".js-review-image-carousel-left",
      arrowRight: ".js-review-image-carousel-right",
      loadMoreFn: loadMore
    });
  }

  initGLightbox();
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1 && node.matches(".carousel-glightbox")) {
          initGLightbox();
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
};
